import React, { PropsWithChildren, useState } from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { usePayHealthStipendMutation } from 'queries/payments/usePayHealthStipendMutation'

type PayHealthStipendFlowContextState = {
  state: {
    isOpen: boolean
    mutationState: {
      isPending: boolean
      isSuccess: boolean
      isError: boolean
      errorMessage: string | undefined
      data: ReturnType<typeof usePayHealthStipendMutation>['data']
    }
  }
  actions: {
    open: () => void
    close: () => void
    submit: ReturnType<typeof usePayHealthStipendMutation>['mutate']
    reset: ReturnType<typeof usePayHealthStipendMutation>['reset']
  }
}

const [Context, usePayHealthStipendFlowContextValue] =
  createContext<PayHealthStipendFlowContextState>({
    name: 'PayHealthStipendFlowContext',
  })

export const PayHealthStipendFlowProvider = ({
  children,
}: PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false)
  const { mutate, reset, isPending, isSuccess, isError, data, error } =
    usePayHealthStipendMutation()

  const value: PayHealthStipendFlowContextState = {
    state: {
      isOpen,
      mutationState: {
        isPending,
        isSuccess,
        isError,
        errorMessage: isError
          ? parseErrorMessage(error) ||
            'Health stipend payout failed. Try again or contact engineering.'
          : undefined,
        data,
      },
    },
    actions: {
      open: () => setIsOpen(true),
      close: () => {
        reset()
        setIsOpen(false)
      },
      submit: mutate,
      reset,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { usePayHealthStipendFlowContextValue }
